// Settlement rendering pieces — the instanced per-form batches, chimney
// smoke and window lights. Split out of Settlements.tsx; geometry lives in
// settlementForms.ts, layout builders in settlementBuild.ts.

import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { dayNightState, nightFactor } from './DayNightLighting.js';
import { RenderLayer } from './RenderLayer.js';
import { hexNoise } from './elevation.js';
import type { SettlementBuilding } from './settlementBuild.js';
import { formParts } from './settlementCultures.js';
import { type BuildingForm, material } from './settlementForms.js';

// --- Rendering ---

export function FormBatch({
  form,
  buildings,
  onClick,
}: {
  form: BuildingForm;
  buildings: SettlementBuilding[];
  onClick?: (cityId: string) => void;
}) {
  const wallsRef = useRef<THREE.InstancedMesh>(null);
  const roofRef = useRef<THREE.InstancedMesh>(null);
  const parts = formParts(form);

  useEffect(() => {
    const obj = new THREE.Object3D();
    const col = new THREE.Color();
    for (const [mesh, key] of [
      [wallsRef.current, 'wallColor'],
      [roofRef.current, 'roofColor'],
    ] as const) {
      if (!mesh) continue;
      buildings.forEach((b, i) => {
        obj.position.set(b.x, b.y, b.z);
        obj.rotation.set(b.rotZ ?? 0, b.rotY, 0);
        obj.scale.set(b.scaleXZ, b.scaleY, b.scaleXZ);
        obj.updateMatrix();
        mesh.setMatrixAt(i, obj.matrix);
        const rgb = b[key];
        mesh.setColorAt(i, col.setRGB(rgb[0], rgb[1], rgb[2]));
      });
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      mesh.computeBoundingSphere();
    }
  }, [buildings]);

  const handleClick = (e: { stopPropagation: () => void; instanceId?: number }) => {
    e.stopPropagation();
    const b = e.instanceId === undefined ? undefined : buildings[e.instanceId];
    if (b) onClick?.(b.cityId);
  };

  return (
    <group>
      <instancedMesh
        name="settlement-walls"
        ref={wallsRef}
        args={[parts.walls, material, buildings.length]}
        castShadow
        renderOrder={RenderLayer.Buildings}
        onClick={handleClick}
      />
      <instancedMesh
        ref={roofRef}
        args={[parts.roof, material, buildings.length]}
        castShadow
        renderOrder={RenderLayer.Buildings}
        onClick={handleClick}
      />
    </group>
  );
}

// --- Chimney smoke ---

/** Roof-ridge height per house form, in authored units (before scaleY). */
const RIDGE: Partial<Record<BuildingForm, number>> = {
  cottage: 0.27,
  longhouse: 0.27,
  townhouse: 0.41,
  // Culture roofs: steep Nordic gables stand taller, hip / swept roofs a little lower.
  'cottage:imperial': 0.24,
  'longhouse:imperial': 0.24,
  'townhouse:imperial': 0.37,
  'cottage:nordic': 0.36,
  'longhouse:nordic': 0.36,
  'townhouse:nordic': 0.5,
  'cottage:eastern': 0.26,
  'longhouse:eastern': 0.26,
  'townhouse:eastern': 0.4,
};

/**
 * Chimney tops for a share (~40%) of the intact houses — ruins, keeps and
 * walls don't smoke. Deterministic from building position.
 */
export function chimneyPoints(
  buildings: readonly SettlementBuilding[],
): Array<[number, number, number]> {
  const out: Array<[number, number, number]> = [];
  for (const b of buildings) {
    const ridge = RIDGE[b.form];
    if (ridge === undefined) continue;
    if (hexNoise(b.x * 3.7, b.z * 2.9) > 0.4) continue;
    // Chimney sits a little off the ridge centre, along the ridge.
    const along = 0.07 * b.scaleXZ;
    out.push([
      b.x + Math.cos(b.rotY) * along,
      b.y + ridge * b.scaleY,
      b.z - Math.sin(b.rotY) * along,
    ]);
  }
  return out;
}

const PUFFS = 5;
const PUFF_LIFE = 5; // seconds
const puffGeo = new THREE.IcosahedronGeometry(1, 0);
const puffMat = new THREE.MeshBasicMaterial({
  color: '#dfe3e6',
  transparent: true,
  opacity: 0.5,
  depthWrite: false,
  fog: true,
});

export function ChimneySmoke({ points }: { points: Array<[number, number, number]> }) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const obj = useMemo(() => new THREE.Object3D(), []);
  useFrame(({ clock }) => {
    const mesh = ref.current;
    if (!mesh) return;
    const t = clock.getElapsedTime();
    points.forEach(([x, y, z], e) => {
      const seed = hexNoise(x * 5.1, z * 4.3);
      for (let k = 0; k < PUFFS; k++) {
        // Age in [0,1): staggered per puff and per chimney.
        const age = (t / PUFF_LIFE + k / PUFFS + seed) % 1;
        const fade = Math.min(1, (1 - age) / 0.25) * Math.min(1, age / 0.08);
        const size = (0.025 + age * 0.07) * fade;
        obj.position.set(
          x + age * 0.12 + Math.sin(t * 0.8 + k) * 0.01,
          y + 0.03 + age * 0.55,
          z + age * 0.05,
        );
        obj.scale.setScalar(Math.max(size, 1e-4));
        obj.updateMatrix();
        mesh.setMatrixAt(e * PUFFS + k, obj.matrix);
      }
    });
    mesh.instanceMatrix.needsUpdate = true;
  });
  if (points.length === 0) return null;
  return (
    <instancedMesh
      ref={ref}
      args={[puffGeo, puffMat, points.length * PUFFS]}
      frustumCulled={false}
      renderOrder={RenderLayer.Buildings + 1}
    />
  );
}

// --- Window lights (day-night-and-weather spec, N1) ---

/** A lit window: world position of the pane centre and the house's rotY. */
export interface WindowLight {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly rotY: number;
}

/** Mid-wall height per house form (authored units, before scaleY). */
const WALL_MID: Partial<Record<BuildingForm, number>> = {
  cottage: 0.08,
  longhouse: 0.08,
  townhouse: 0.16,
};
const HOUSE_DEPTH: Partial<Record<BuildingForm, number>> = {
  cottage: 0.2,
  longhouse: 0.2,
  townhouse: 0.2,
};

/**
 * Lit windows for ~55% of the intact houses — one pane on a deterministic
 * face (front or back, +Z/-Z local), ruins/keeps/walls stay dark.
 * Deterministic from building position so a lit house stays lit.
 */
export function windowLights(buildings: readonly SettlementBuilding[]): WindowLight[] {
  const out: WindowLight[] = [];
  for (const b of buildings) {
    const mid = WALL_MID[b.form];
    const depth = HOUSE_DEPTH[b.form];
    if (mid === undefined || depth === undefined) continue;
    const h = hexNoise(b.x * 3.3, b.z * 2.7);
    if (h > 0.55) continue; // ~55% lit
    // Pane sits on the +Z (front) face when h < 0.28, else the -Z face.
    const face = h < 0.28 ? 1 : -1;
    const off = (depth / 2) * b.scaleXZ + 0.005;
    const sin = Math.sin(b.rotY);
    const cos = Math.cos(b.rotY);
    // Rotate the local (0, mid, face*off) offset into world space.
    out.push({
      x: b.x + sin * face * off,
      y: b.y + mid * b.scaleY,
      z: b.z + cos * face * off,
      rotY: b.rotY + (face === 1 ? 0 : Math.PI),
    });
  }
  return out;
}

const WIN_W = 0.05;
const WIN_H = 0.04;
const windowGeo = new THREE.PlaneGeometry(WIN_W, WIN_H);
const windowMat = new THREE.MeshBasicMaterial({
  color: '#ffca6e',
  transparent: true,
  opacity: 0,
  depthWrite: false,
});

export function WindowLights({ windows }: { readonly windows: readonly WindowLight[] }) {
  const ref = useRef<THREE.InstancedMesh>(null);

  useEffect(() => {
    const mesh = ref.current;
    if (!mesh) return;
    const obj = new THREE.Object3D();
    windows.forEach((w, i) => {
      obj.position.set(w.x, w.y, w.z);
      obj.rotation.set(0, w.rotY, 0);
      obj.scale.setScalar(1);
      obj.updateMatrix();
      mesh.setMatrixAt(i, obj.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();
  }, [windows]);

  // All panes share one material — fade the whole town with nightFactor.
  useFrame(() => {
    const f = nightFactor(dayNightState.sunHeight);
    windowMat.opacity = 0.95 * f;
    windowMat.visible = f > 0.02;
  });

  if (windows.length === 0) return null;
  return (
    <instancedMesh
      args={[windowGeo, windowMat, windows.length]}
      ref={ref}
      frustumCulled={false}
      renderOrder={RenderLayer.Buildings + 1}
    />
  );
}
